import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Lock, CheckCircle, XCircle } from "@phosphor-icons/react";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section, Loader } from "@/components/Blocks";
import { RiskNotice } from "@/components/layout/PublicLayout";
import { Input } from "@/components/ui/input";
import { AttendeeFields, getUtm } from "@/components/AttendeeFields";
import { VerifyBanner } from "@/pages/LaunchPages";
import { PriceCards, IndicatorNotice } from "@/components/PriceCards";

const onSale = (e) => (e.price_tiers || []).filter((t) => t.on_sale);


const Steps = ({ step }) => (
  <ol className="mb-8 flex gap-2" data-testid="checkout-stepper">
    {["Seats", "Attendees", "Review & pay"].map((s, i) => (
      <li key={s} className={`flex-1 border-t-4 pt-2 text-xs font-bold uppercase tracking-widest ${i <= step ? "border-[#E0A21B] text-[#0B1B33]" : "border-[#0B1B33]/10 text-[#475569]"}`}>
        {i + 1}. {s}
      </li>
    ))}
  </ol>
);

const Summary = ({ e, quote }) => (
  <div className="panel p-6" data-testid="order-summary">
    <div className="eyebrow">Order summary</div>
    <div className="mt-3 font-display text-2xl font-bold uppercase">{e.title}</div>
    <div className="text-sm text-[#475569]">{fmtDate(e.start_at, e.city?.timezone)}{e.venue?.name ? ` · ${e.venue.name}` : " · Venue announced soon"}</div>
    {quote && (
      <dl className="mt-5 space-y-2 text-sm">
        <div className="flex justify-between"><dt>{quote.tier.name} × {quote.quantity}</dt><dd className="font-mono-num">{money(quote.subtotal_cents)}</dd></div>
        {quote.discount_cents > 0 && <div className="flex justify-between text-[#1E8E5A]"><dt>{quote.coupon_kind === "scholarship" ? "Scholarship" : "Coupon"} {quote.coupon_code}</dt><dd className="font-mono-num">−{money(quote.discount_cents)}</dd></div>}
        {quote.referral_discount_cents > 0 && <div className="flex justify-between text-[#1E8E5A]" data-testid="referral-discount-line"><dt>{quote.referral_note}</dt><dd className="font-mono-num">−{money(quote.referral_discount_cents)}</dd></div>}
        {quote.taxes.map((t) => <div key={t.label} className="flex justify-between text-[#475569]"><dt>{t.label} ({t.rate_bp / 100}%)</dt><dd className="font-mono-num">{money(t.amount_cents)}</dd></div>)}
        {quote.credit_applied_cents > 0 && <div className="flex justify-between text-[#1E8E5A]" data-testid="credit-applied-line"><dt>Account credit</dt><dd className="font-mono-num">−{money(quote.credit_applied_cents)}</dd></div>}
        {quote.gift_applied_cents > 0 && <div className="flex justify-between text-[#1E8E5A]" data-testid="gift-applied-line"><dt>Gift card {quote.gift_card_code}</dt><dd className="font-mono-num">−{money(quote.gift_applied_cents)}</dd></div>}
        <div className="flex justify-between border-t pt-3 text-base font-bold"><dt>Total (US$)</dt><dd className="font-mono-num" data-testid="order-total">{money(quote.total_cents)}</dd></div>
      </dl>
    )}
    {quote?.referral_note && !quote.referral_discount_cents && <p className="mt-3 text-xs text-[#C8382E]" data-testid="referral-note">{quote.referral_note}</p>}
  </div>
);

export default function Checkout() {
  const [sp] = useSearchParams();
  const loc = useLocation();
  const { user } = useAuth();
  const { data: e, loading } = useFetch(`/events/${sp.get("event")}`);
  const [step, setStep] = useState(0);
  const [tier, setTier] = useState(sp.get("tier") || "");
  const [qty, setQty] = useState(1);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState("");
  const [names, setNames] = useState([]);
  const [details, setDetails] = useState([]);
  const [mkt, setMkt] = useState(false);
  const [quote, setQuote] = useState(null);
  const [ack, setAck] = useState(false);
  const [indAck, setIndAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [useCredit, setUseCredit] = useState(false);
  const [gift, setGift] = useState("");
  const [giftApplied, setGiftApplied] = useState("");
  const [sigs, setSigs] = useState([]);
  const keyRef = useRef(crypto.randomUUID());
  const norm = (s) => s.toLowerCase().split(/\s+/).filter(Boolean).join(" ");
  const allSigned = names.length > 0 && names.every((n, i) => sigs[i]?.accepted && norm(sigs[i]?.signed_name || "") === norm(n));

  useEffect(() => {
    if (!e) return;
    const t = onSale(e).some((x) => x.code === tier) ? tier : onSale(e)[0]?.code;
    if (!t) return;
    api.post("/checkout/quote", { event_id: e.id, tier_code: t, quantity: qty, coupon_code: applied, use_credit: useCredit, gift_card_code: giftApplied })
      .then((r) => setQuote(r.data))
      .catch((err) => { toast.error(errMsg(err)); setApplied(""); setGiftApplied(""); });
  }, [e, tier, qty, applied, useCredit, giftApplied, user]);

  useEffect(() => {
    setNames((n) => Array.from({ length: qty }, (_, i) => n[i] || (i === 0 && user ? user.name : "")));
    setSigs((s) => Array.from({ length: qty }, (_, i) => s[i] || { signed_name: "", accepted: false }));
  }, [qty, user]);

  if (loading) return <Loader />;
  if (!e) return <Section><p>Event not found.</p></Section>;
  if (!e.registration?.open) return <Section><p data-testid="checkout-registration-closed">Coming soon – registration is closed for this class. <Link to={`/events/${e.slug}`} className="underline">Get notified when it opens</Link>.</p></Section>;
  if (!user) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return (
      <Section>
        <div className="mx-auto max-w-lg panel p-8 text-left" data-testid="checkout-auth-gate">
          <h1 className="font-display text-4xl font-black uppercase">Almost there</h1>
          <p className="mt-2 text-[#475569]">Sign in or create a free student account so we can send your tickets and receipts.</p>
          <div className="mt-6 flex gap-3">
            <Link to={`/register?next=${next}`} className="btn-gold" data-testid="checkout-register-btn">Create account</Link>
            <Link to={`/login?next=${next}`} className="btn-outline" data-testid="checkout-login-btn">Sign in</Link>
          </div>
        </div>
      </Section>
    );
  }

  const pay = async (attempt = 0) => {
    const key = keyRef.current;
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", {
        event_id: e.id, tier_code: quote.tier.code, quantity: qty, coupon_code: applied,
        attendee_names: names, origin_url: window.location.origin, risk_ack: ack && allSigned, indicator_ack: indAck, use_credit: useCredit,
        gift_card_code: giftApplied, signatures: sigs, marketing_consent: mkt, utm: getUtm(),
        attendees: names.map((n, i) => ({ full_name: n, ...(details[i] || {}) })), client_key: key,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const st = err?.response?.status;
      if ((!st || [502, 503, 504].includes(st)) && attempt < 150) {
        setWaiting(true);
        setTimeout(() => pay(attempt + 1), 4000);
        return;
      }
      keyRef.current = crypto.randomUUID();
      setWaiting(false);
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <Section>
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <h1 className="mb-6 font-display text-5xl font-black uppercase">Checkout</h1>
          <Steps step={step} />
          <VerifyBanner />
          {step === 0 && (
            <div className="panel space-y-5 p-6">
              <PriceCards tiers={e.price_tiers} tz={e.city?.timezone} selected={quote?.tier.code} onSelect={setTier} tierLeft={e.tier_left} vipBenefits={e.vip_benefits} />
              <div className="flex items-center justify-between">
                <span className="font-semibold">Seats</span>
                <div className="flex items-center gap-3">
                  <button className="btn-outline px-3 py-2" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus-btn" aria-label="Fewer seats"><Minus /></button>
                  <span className="w-8 text-center font-mono-num text-lg" data-testid="qty-value">{qty}</span>
                  <button className="btn-outline px-3 py-2" onClick={() => setQty(Math.min(10, e.seats_left, e.tier_left?.[quote?.tier.code] ?? 10, qty + 1))} data-testid="qty-plus-btn" aria-label="More seats"><Plus /></button>
                </div>
              </div>
              <div className="flex gap-2">
                <Input placeholder="Coupon code" value={coupon} onChange={(x) => setCoupon(x.target.value)} data-testid="coupon-input" />
                <button className="btn-outline" onClick={() => setApplied(coupon.trim().toUpperCase())} data-testid="coupon-apply-btn">Apply</button>
              </div>
              <div className="flex gap-2">
                <Input placeholder="Gift card code" value={gift} onChange={(x) => setGift(x.target.value)} data-testid="giftcard-code-input" />
                <button className="btn-outline" onClick={() => setGiftApplied(gift.trim().toUpperCase())} data-testid="giftcard-apply-btn">Apply</button>
              </div>
              <button className="btn-gold w-full" onClick={() => setStep(1)} data-testid="checkout-next-1">Continue</button>
            </div>
          )}
          {step === 1 && (
            <div className="panel space-y-4 p-6">
              <p className="text-sm text-[#475569]">Tickets are name-based. One buyer can book several named attendees.</p>
              {names.map((n, i) => (
                <AttendeeFields key={i} i={i} name={n} onName={(v) => setNames(names.map((x, j) => (j === i ? v : x)))} d={details[i] || {}} onChange={(v) => setDetails(Array.from({ length: names.length }, (_, j) => (j === i ? v : details[j] || {})))} />
              ))}
              <div className="flex gap-3">
                <button className="btn-outline" onClick={() => setStep(0)} data-testid="checkout-back-1">Back</button>
                <button className="btn-gold flex-1" disabled={names.some((n) => !n.trim())} onClick={() => setStep(2)} data-testid="checkout-next-2">Continue</button>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="panel space-y-5 p-6">
              <div className="rounded-md border-l-4 border-[#C8382E] bg-[#C8382E]/5 p-4"><RiskNotice /></div>
              {quote?.credit_balance_cents > 0 && (
                <label className="flex items-center gap-3 rounded-md border border-[#1E8E5A]/30 bg-[#1E8E5A]/5 p-3 text-sm">
                  <input type="checkbox" checked={useCredit} onChange={(x) => setUseCredit(x.target.checked)} data-testid="use-credit-checkbox" />
                  <span>Use my account credit ({money(quote.credit_balance_cents)} available)</span>
                </label>
              )}
              <div className="space-y-3" data-testid="signing-block">
                <p className="text-sm">Each attendee signs the <Link to="/risk-disclosure" target="_blank" className="underline">Risk Warning</Link> and accepts the <Link to="/terms" target="_blank" className="underline">Terms</Link> and <Link to="/refund-policy" target="_blank" className="underline">Refund Policy</Link> by typing their name. A signed PDF is saved to your account.</p>
                {names.map((n, i) => (
                  <div key={i} className="rounded-md border p-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#475569]">Attendee {i + 1}: {n}</div>
                    <Input className="mt-1" placeholder={`Type "${n}" to sign`} value={sigs[i]?.signed_name || ""} onChange={(x) => setSigs(sigs.map((s, j) => (j === i ? { ...s, signed_name: x.target.value } : s)))} data-testid={`sign-name-${i}`} />
                    <label className="mt-2 flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={!!sigs[i]?.accepted} onChange={(x) => setSigs(sigs.map((s, j) => (j === i ? { ...s, accepted: x.target.checked } : s)))} data-testid={`sign-accept-${i}`} /> I have read and sign the Risk Warning, and I accept the Terms and Refund Policy.</label>
                  </div>
                ))}
              </div>
              <label className="flex items-start gap-3 text-sm" data-testid="casl-consent-label"><input type="checkbox" className="mt-1" checked={mkt} onChange={(x) => setMkt(x.target.checked)} data-testid="casl-consent-checkbox" /> Yes, send me news about classes and offers by email. (Optional. You can unsubscribe at any time. ForeXperts AI Inc.)</label>
              <label className="flex items-start gap-3 text-sm">
                <input type="checkbox" checked={ack} onChange={(x) => setAck(x.target.checked)} className="mt-1" data-testid="risk-ack-checkbox" />
                <span>I understand that trading involves risk, that this is an education course only, and I agree to the <Link to="/terms" className="underline">Terms</Link> and <Link to="/refund-policy" className="underline">Refund Policy</Link>.</span>
              </label>
              <IndicatorNotice testid="indicator-notice-checkout" />
              <label className="flex items-start gap-3 text-sm font-semibold">
                <input type="checkbox" checked={indAck} onChange={(x) => setIndAck(x.target.checked)} className="mt-1" data-testid="indicator-ack-checkbox" />
                <span>I understand the indicator is not included in my ticket.</span>
              </label>
              <p className="text-sm font-semibold text-[#0B1B33]" data-testid="checkout-fx-note">Charged in US dollars. Your card issuer may apply a foreign exchange fee.</p>
              <div className="flex gap-3">
                <button className="btn-outline" onClick={() => setStep(1)} data-testid="checkout-back-2">Back</button>
                <button className="btn-gold flex-1" disabled={!ack || !indAck || !allSigned || busy || !quote} onClick={() => pay()} data-testid="checkout-pay-btn">
                  <Lock weight="bold" /> {waiting ? "Please wait…" : busy ? "Confirming…" : quote && quote.total_cents === 0 ? "Confirm booking" : `Pay ${quote ? money(quote.total_cents) : ""} securely`}
                </button>
              </div>
              {waiting && <p className="rounded-md bg-[#FFF8E6] p-3 text-sm font-semibold text-[#9A6B00]" data-testid="checkout-please-wait">Please wait. Lots of people are booking right now and we're holding your place in line. Don't close this page.</p>}
              <p className="text-xs text-[#475569]">{quote && quote.total_cents === 0 ? "Nothing to pay: your booking is confirmed right away." : "You will be taken to Stripe to pay by card. Test mode: use card 4242 4242 4242 4242."}</p>
            </div>
          )}
        </div>
        <div className="lg:col-span-5"><Summary e={e} quote={quote} /></div>
      </div>
    </Section>
  );
}

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const [state, setState] = useState("checking");
  const [orderId, setOrderId] = useState(null);
  useEffect(() => {
    let n = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sp.get("session_id")}`);
        if (data.payment_status === "paid") { setOrderId(data.order_id); setState("paid"); return; }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { /* keep polling */ }
      if (++n < 15 && !stop) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => { stop = true; };
  }, [sp]);
  return (
    <Section>
      <div className="mx-auto max-w-xl panel p-8" data-testid="payment-result">
        {state === "checking" && <Loader label="Confirming your payment" />}
        {state === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-[#1E8E5A]" />
            <h1 className="mt-4 font-display text-5xl font-black uppercase" data-testid="payment-success-title">You're cleared for launch</h1>
            <p className="mt-2 text-[#475569]">Your payment went through. Your tickets with QR codes are in your student app, and a confirmation email is on its way.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/app/tickets" className="btn-gold" data-testid="view-tickets-btn">View my tickets</Link>
              <Link to="/app/referrals" className="btn-outline" data-testid="bring-friend-btn">Bring a friend</Link>
              {orderId && <Link to={`/app/receipts/${orderId}`} className="btn-outline" data-testid="view-receipt-btn">View receipt</Link>}
            </div>
            <IndicatorNotice testid="indicator-notice-confirmation" className="mt-6" />
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={56} weight="fill" className="text-[#C8382E]" />
            <h1 className="mt-4 font-display text-4xl font-black uppercase">{state === "failed" ? "Payment not completed" : "Still confirming"}</h1>
            <p className="mt-2 text-[#475569]">{state === "failed" ? "The checkout session expired. No charge was made." : "This is taking longer than usual. Check your student app in a minute."}</p>
            <Link to="/app/registrations" className="btn-outline mt-6">Go to my registrations</Link>
          </>
        )}
      </div>
    </Section>
  );
}

export function PaymentCancel() {
  const [sp] = useSearchParams();
  return (
    <Section>
      <div className="mx-auto max-w-xl panel p-8" data-testid="payment-cancel">
        <h1 className="font-display text-4xl font-black uppercase">Checkout cancelled</h1>
        <p className="mt-2 text-[#475569]">No payment was taken. Your seat hold will be released in 30 minutes.</p>
        <Link to={sp.get("event") ? `/events/${sp.get("event")}` : "/cities"} className="btn-gold mt-6" data-testid="back-to-event-btn">Back to the event</Link>
      </div>
    </Section>
  );
}
